"use client";

import Image from "next/image";
import useTheme from "src/context/appThemeContext";
import React, { useEffect, useState } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import TychoButton from "src/components/atoms/buttons/TychoButton";

export const videoPortfolioData = {
    subtitle: " VIDEO EDITING WORK",
    title: "Video Editing Portfolio",

    videos: [
        {
            id: 1,
            title: "Cinematic Travel Edit",
            youtubeId: "flYYG65yW5I",
            youtubeUrl: "https://www.youtube.com/watch?v=flYYG65yW5I",
            thumbnail: "https://img.youtube.com/vi/flYYG65yW5I/hqdefault.jpg",
            editingType: "Travel",
            duration: "3:05",
            description:
                "A visually stunning travel montage crafted with smooth cinematic transitions, speed ramping, and immersive storytelling. Professional color grading enhances every scene while dynamic camera movements create a premium viewing experience. Carefully synchronized music and sound effects elevate the emotional impact, resulting in a polished and engaging travel film.",
            technologies: [
                "Adobe Premiere Pro",
                "After Effects",
                "DaVinci Resolve",
            ],
        },

        {
            id: 2,
            title: "Product Advertisement",
            youtubeId: "jQaKhWnWk0A",
            youtubeUrl: "https://www.youtube.com/watch?v=jQaKhWnWk0A",
            thumbnail: "https://img.youtube.com/vi/jQaKhWnWk0A/hqdefault.jpg",
            editingType: "Commercial",
            duration: "1:42",
            description:
                "A premium commercial edit designed to highlight the product with cinematic visuals and engaging storytelling. Motion graphics, animated text, and seamless transitions maintain viewer attention throughout the video. Advanced color correction and professionally mixed audio deliver a clean, modern, and high-end brand presentation.",
            technologies: [
                "Adobe Premiere Pro",
                "After Effects",
                "Photoshop",
            ],
        },

        {
            id: 3,
            title: "YouTube Talking Head",
            youtubeId: "iBqboGk4H_U",
            youtubeUrl: "https://www.youtube.com/watch?v=iBqboGk4H_U",
            thumbnail: "https://img.youtube.com/vi/iBqboGk4H_U/hqdefault.jpg",
            editingType: "YouTube",
            duration: "8:20",
            description:
                "A professional YouTube edit featuring clean jump cuts, engaging zoom effects, and animated captions that improve viewer retention. Strategic B-roll placement keeps the content visually interesting while sound effects and background music create a polished and immersive viewing experience suitable for modern creators.",
            technologies: [
                "Adobe Premiere Pro",
                "After Effects",
            ],
        },

        {
            id: 4,
            title: "Instagram Reel",
            youtubeId: "2tTKhVR0cJw",
            youtubeUrl: "https://www.youtube.com/watch?v=2tTKhVR0cJw",
            thumbnail: "https://img.youtube.com/vi/2tTKhVR0cJw/hqdefault.jpg",
            editingType: "Short Form",
            duration: "00:48",
            description:
                "A high-retention vertical reel optimized for social media platforms with fast-paced editing and dynamic transitions. Animated subtitles, motion graphics, and trending visual effects maximize engagement while maintaining a smooth storytelling flow. The final edit is designed to capture attention within the first few seconds.",
            technologies: [
                "CapCut",
                "After Effects",
            ],
        },

        {
            id: 5,
            title: "Brand Promo Video",
            youtubeId: "XvarnZcw_Zc",
            youtubeUrl: "https://www.youtube.com/watch?v=XvarnZcw_Zc",
            thumbnail: "https://img.youtube.com/vi/XvarnZcw_Zc/hqdefault.jpg",
            editingType: "Promo",
            duration: "2:15",
            description:
                "A modern promotional video showcasing the brand through cinematic visuals, elegant typography, and smooth scene transitions. Creative motion graphics, professional sound design, and premium color grading reinforce the brand identity while delivering a memorable and impactful viewing experience for potential customers.",
            technologies: [
                "Adobe Premiere Pro",
                "After Effects",
                "DaVinci Resolve",
            ],
        },
    ],
};

const EditingProjectOverView = ({ data = videoPortfolioData }) => {
    const { subtitle = "OUR VIDEO WORK", title = "Video Editing Portfolio" } = data || {};
    const videos = data?.videos || [];

    const { theme, mounted } = useTheme();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [visibleCards, setVisibleCards] = useState(4);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            let cards = 1;
            if (width >= 1536) {
                cards = 4;
            } else if (width >= 1440) {
                cards = 3;
            } else if (width >= 768) {
                cards = 3;
            }

            setVisibleCards(cards);
            const maxIndex = Math.max(0, videos.length - cards);
            setCurrentIndex((prev) => Math.min(prev, maxIndex));
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [videos.length]);

    const maxIndex = Math.max(0, videos.length - visibleCards);

    const nextSlide = () => setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    const prevSlide = () => setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));

    const getCardWidth = () => `calc((100% - ${(visibleCards - 1) * 24}px) / ${visibleCards})`;

    return (
        <section className="mb-10! overflow-hidden mt-10!">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10!">
                <div>
                    {subtitle && (
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-xs font-bold uppercase tracking-widest block mb-3!">
                            {subtitle}
                        </span>
                    )}
                    <h2 className="text-2xl lg:text-5xl font-bold tracking-tight text-text-primary">{title}</h2>
                </div>

                <TychoButton
                    size="lg"
                    variant="secondary"
                    href="/success-stories"
                    title="View All Projects"
                    className="px-4! py-2! text-sm self-end w-full md:w-[170px]"
                />
            </div>

            {videos.length > 0 && (
                <div className="relative w-full overflow-hidden">
                    {videos.length > visibleCards && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="absolute left-0 top-[240px] -translate-y-1/2 z-30 w-12 h-12 rounded-full border border-border-primary bg-surface-primary shadow-md cursor-pointer hover:bg-surface-secondary"
                            >
                                <LuChevronLeft size={24} className="mx-auto ml-2!" />
                            </button>
                            <button
                                onClick={nextSlide}
                                className="absolute right-0 top-[240px] -translate-y-1/2 z-30 w-12 h-12 rounded-full border border-border-primary bg-surface-primary shadow-md cursor-pointer hover:bg-surface-secondary"
                            >
                                <LuChevronRight size={24} className="mx-auto ml-3!" />
                            </button>
                        </>
                    )}

                    <div className="overflow-hidden">
                        <div
                            className="flex gap-6 transition-transform duration-500 ease-in-out"
                            style={{ transform: `translateX(calc(-${currentIndex} * (${getCardWidth()} + 24px)))` }}
                        >
                            {videos.map((video, idx) => (
                                <div
                                    key={video.id}
                                    className="shrink-0 rounded-2xl border border-border-primary overflow-hidden flex flex-col min-h-[520px] group"
                                    style={{ width: getCardWidth(), background: "var(--card-background)", boxShadow: "var(--card-shadow)" }}
                                >
                                    <div className="relative h-[240px] overflow-hidden border-b border-border-primary/40">
                                        <Image
                                            src={video.thumbnail}
                                            alt={video.title}
                                            fill
                                            className="object-cover group-hover:scale-105 transition-transform"
                                        />
                                        <a
                                            href={video.youtubeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition"
                                        >
                                            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center text-3xl text-white">▶</div>
                                        </a>
                                    </div>

                                    <div className="p-5! flex flex-col flex-1 justify-between">
                                        <div>
                                            <div className="flex justify-between">
                                                <h3 className="text-xl font-bold text-text-primary">{video.title}</h3>
                                                <span className="text-sm font-mono text-text-secondary">{video.duration}</span>
                                            </div>

                                            <span className="inline-block mt-3! bg-violet-500/10 text-violet-400 text-xs font-bold uppercase tracking-wider px-3! py-1! rounded-full border border-violet-500/20">
                                                {video.editingType}
                                            </span>

                                            <p className="mt-5! text-text-secondary text-sm line-clamp-3">{video.description}</p>
                                        </div>

                                        {/* Technology Tags (Text Only) */}
                                        <div className="mt-6!">
                                            <p className="text-xs uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 mb-2!">Technology</p>
                                            <div className="flex flex-wrap gap-2">
                                                {video.technologies.map((tech, i) => (
                                                    <span key={i} className="text-xs bg-surface-secondary px-3! py-1! rounded-full border border-border-primary">
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default EditingProjectOverView;